import React, { useRef, useCallback, useEffect, useLayoutEffect, useImperativeHandle, forwardRef } from 'react';
import { Box, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import FindInPageIcon from '@mui/icons-material/FindInPage';
import SignatureCanvas from 'react-signature-canvas';
import FormSection from '../../../components/FormSection';
import SelfieUpload from '../../../components/SelfieUpload';
import { CaregiverFormData } from '../types';
import { FieldErrors } from '../validation';

export interface SignatureStepHandle {
  clearSignature: () => void;
}

interface Props {
  formData: CaregiverFormData;
  fieldErrors: FieldErrors;
  setFormData: React.Dispatch<React.SetStateAction<CaregiverFormData>>;
}

const SignatureStep = forwardRef<SignatureStepHandle, Props>(({ formData, fieldErrors, setFormData }, ref) => {
  const { t } = useTranslation();
  const sigRef = useRef<SignatureCanvas>(null);

  const adjustSignaturePadSize = useCallback(() => {
    if (sigRef.current && (sigRef.current as any).getCanvas) {
      const canvas = (sigRef.current as any).getCanvas() as HTMLCanvasElement;
      const container = canvas.parentNode as HTMLElement;
      if (!container) return;
      const { width } = container.getBoundingClientRect();

      canvas.width = width;
      canvas.style.width = `${width}px`;
      canvas.height = 200;
      canvas.style.height = '200px';
    }
  }, []);

  useLayoutEffect(() => {
    adjustSignaturePadSize();
  }, [adjustSignaturePadSize]);

  useEffect(() => {
    const timeout = setTimeout(adjustSignaturePadSize, 100);
    window.addEventListener('resize', adjustSignaturePadSize);
    return () => {
      clearTimeout(timeout);
      window.removeEventListener('resize', adjustSignaturePadSize);
    };
  }, [adjustSignaturePadSize]);

  const handleEnd = () => {
    if (sigRef.current && !sigRef.current.isEmpty()) {
      const dataUrl = sigRef.current.toDataURL('image/png');
      setFormData((prev) => ({ ...prev, portalSignature: dataUrl }));
    }
  };

  useImperativeHandle(ref, () => ({
    clearSignature: () => {
      sigRef.current?.clear();
      setFormData((prev) => ({ ...prev, portalSignature: '' }));
    },
  }));

  return (
    <>
      <SelfieUpload
        existingPhotoUrl={!!formData.contactPhotoBlobKey || !!formData.contactPhotoUrl}
        onPhotoUploaded={(hasPhoto) => {
          setFormData((prev) => ({
            ...prev,
            contactPhotoUrl: hasPhoto ? 'uploaded' : '',
          }));
        }}
        required={false}
      />

      <FormSection title={t('form.signature', 'Signature')} icon={<FindInPageIcon />}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t(
            'form.signatureInstructions',
            'By signing below, I certify that all information provided is true and accurate to the best of my knowledge.'
          )}
        </Typography>

      <Box
        sx={{
          border: fieldErrors.portalSignature ? '2px solid #f44336' : '1px solid #000000',
          width: '100%',
          height: 200,
          borderRadius: 1,
          mb: 2,
        }}
      >
        <SignatureCanvas
          ref={sigRef}
          onEnd={handleEnd}
          canvasProps={{
            height: 200,
            className: 'signatureCanvas',
          }}
        />
      </Box>

      {fieldErrors.portalSignature && (
        <Typography color="error" variant="body2" sx={{ textAlign: 'center' }}>
          {fieldErrors.portalSignature}
        </Typography>
      )}
    </FormSection>
    </>
  );
});

SignatureStep.displayName = 'SignatureStep';

export default SignatureStep;
